/**
 * Find the map point for an FPA member's parcel.
 *
 * A member can be tied to a parcel two different ways, and only one of them
 * still works:
 *
 *   - `FPAMember.parcel_id` → the legacy DynamoDB parcels table. The member
 *     detail response still hydrates `parcel_boundary` from it, but nothing
 *     writes that table any more (see the note atop `@/api/parcels`), so in
 *     practice the field arrives null.
 *   - A `ParcelOwner` row keyed on the cadastral LISKEY. This is the live
 *     link — it is what the parcel map and `MemberLinkDialog` write.
 *
 * Because the share builder only ever looked at `parcel_boundary`, member
 * shares went out with no coordinates and no directions link. This resolves
 * the live link as well, and keeps the legacy path as a fallback so any tenant
 * still carrying `parcel_id` links is unaffected.
 *
 * The two-call fallback (links → parcel) is the same resolution the member
 * detail page already performs to draw its highlight; callers that already
 * hold either piece should pass it in and skip the round-trips.
 */

import { listLinkedParcelRefs, getTenantParcelByRef } from '@/api/parcels';
import { polygonCentroid } from '@/utils/share';
import { parcelToPolygons, type ParcelRecord } from '@/utils/parcelGeometry';

/** The bits of a hydrated member this module reads. Loose — the .jsx callers hand over raw API responses. */
export interface CentroidResolvableMember {
  id?: number | string | null;
  tenant_id?: number | null;
  parcel_boundary?: { type?: string; coordinates?: unknown } | null;
}

export interface ResolveCentroidOptions {
  /** A parcel already in hand (e.g. from `parcelAtPoint`, or the detail page's highlight). */
  parcel?: ParcelRecord | null;
  /** A known LISKEY for this member, skipping the links lookup. */
  parcelRef?: string | null;
}

/**
 * Centroid of a parcel record as Leaflet `[lat, lng]`.
 *
 * Prefers the precomputed centroid the Parquet pipeline ships; falls back to
 * averaging the geometry, which is what a record fetched without the enrichment
 * columns leaves us with.
 */
export function parcelRecordCentroid(
  parcel: ParcelRecord | null | undefined,
): [number, number] | null {
  if (!parcel) return null;
  const { centroid_lat: lat, centroid_lon: lng } = parcel;
  if (typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)) {
    return [lat, lng];
  }
  return polygonCentroid(parcelToPolygons(parcel).map((poly) => poly.positions));
}

/** Centroid of the GeoJSON boundary carried on a hydrated member, if any. */
export function memberBoundaryCentroid(
  member: CentroidResolvableMember | null | undefined,
): [number, number] | null {
  if (!member?.parcel_boundary) return null;
  const rings = parcelToPolygons({ geometry: member.parcel_boundary }).map(
    (poly) => poly.positions,
  );
  return polygonCentroid(rings);
}

/**
 * Best available `[lat, lng]` for a member's parcel, or null when they have none.
 *
 * Cheapest source first, so a caller holding the parcel pays nothing. Every
 * network step is non-fatal: a member with no link, or a caller without
 * `fpa_member_read` (whose `links` array comes back empty), simply gets null
 * and the share drops its coordinate rows rather than failing.
 */
export async function resolveMemberCentroid(
  member: CentroidResolvableMember | null | undefined,
  opts: ResolveCentroidOptions = {},
): Promise<[number, number] | null> {
  const fromParcel = parcelRecordCentroid(opts.parcel);
  if (fromParcel) return fromParcel;

  const fromBoundary = memberBoundaryCentroid(member);
  if (fromBoundary) return fromBoundary;

  const tenantId = member?.tenant_id;
  if (tenantId == null) return null;

  try {
    let ref = opts.parcelRef ?? null;
    if (!ref) {
      const linkData = await listLinkedParcelRefs(tenantId);
      const match = (linkData?.links || []).find(
        (l) => String(l.member_id) === String(member?.id),
      );
      ref = match?.parcel_ref ?? null;
    }
    if (!ref) return null;

    const parcel = await getTenantParcelByRef(tenantId, ref);
    return parcelRecordCentroid(parcel as ParcelRecord | null);
  } catch (err) {
    console.warn('Could not resolve the parcel centroid for this member:', err);
    return null;
  }
}
